/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, OnDestroy } from '@angular/core';

import moment from 'moment-timezone';
import { includes } from 'lodash';
import { of , Observable, BehaviorSubject, Subscription, map, catchError, tap, filter } from 'rxjs';

import { MentionsSearchResponseModel } from '../../../api/mentions/model/mentions-search-response.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ReportBlockTypeEnum } from '../enums/report-block-type.enum';
import { DataPointModel } from '../../Charts/models/data-point.model';
import { MentionSearchFacetTypesEnum } from '../../../api/mentions/enum/mention-search-facet-types.enum';
import { chartColors } from '../../Charts/enums/chart-colors.enum';
import { MediaTypeEnum, mediaTypeColorKeyMap } from '../../Basic/enums/media-type.enum';
import { PieChartLabelModel } from '../../../Monitor/Shared/models/pie-chart-label.model';
import { JournodexApiService } from '../../../api/journodex/journodex.api.service';
import { ReportSeriesModel } from '../models/report-series.model';
import { DateFacetValueModel } from '../../../api/mentions/model/date-facet-value.model';
import { JournodexRegionsService } from '../../Journodex/services/journodex-regions.service';
import { JournodexGeographyModel } from '../../Journodex/models/journodex-geography.model';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { JournodexGeographicEntityModel } from '../../Journodex/models/journodex-geographic-entity.model';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { FacetResponseModel } from '../../../api/mentions/model/facet-response.model';
import { ChartMetricsEnum } from '../enums/chart-metrics.enum';
import { FacetValueModel } from '../../../api/mentions/model/facet-value.model';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { MediaTypeModel } from '../../../Monitor/Shared/models/media-type.model';
import { SentimentModel } from '../../../Monitor/Shared/models/sentiment.model';
import { JournodexSourceModel } from '../../Journodex/models/journodex-source.model';
import { SearchCriteriaSourcesEnum } from '../../../api/mentions/enum/search-criteria-sources.enum';
import { ProminenceLockerService } from '../../Misc/services/prominence-locker.service';
import { ProminenceRecalculationJobStatus } from '../../../api/monitoring/monitoring-account-configuration.api';
import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { userAppPreferencesDefault } from '../../../Core/Users/data/user-app-preferences-default';
import { UserAppPreferenceChartColors } from '../../../Core/Users/models/user-app-preferences-chart-colors.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { PreferencesModel } from '../../../Core/Users/models/preferences.model';
import { monitoringFiltersEnum } from '../../../Monitor/Shared/enums/monitoring-filters.enum';
import { BucketResponseModel } from '../../../api/mentions/model/bucket-response.model';
import { ReportsNeoFacetKeys } from '../enums/reports-neo-facet-keys.enum';
import { NeoFacetResponseModel } from '../../../api/mentions/model/neo-facet-response.model';
import { safeDivide } from '../../../Core/utils';
import { TermTypesEnum } from '../enums/term-types.enum';
import { CurrencyConversionService } from '../../../Core/Users/services/currency-conversion.service';
import { UserCurrency } from '../../../Core/Users/enums/user-currency';
import { MonitorConfigService } from '../../MonitorShared/services/monitor-config.service';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { MonitoringKeywordGroupModel } from '../../../Monitor/Shared/models/monitoring-keyword-group.model';
import { ChartSpecificTypesEnum } from '../enums/chart-specific-types.enum';
import { getTimeScale } from '../../../Common/Forms/helpers/time.helper';
import { MentionSearchQueryModel } from '../../../Monitor/Shared/models/mention-search-query.model';


@Injectable()
export class ReportsSeriesMapperService implements OnDestroy {

    subscriptions: Subscription[] = [];
    prominenceStatus: ProminenceRecalculationJobStatus;

    private userChartColors: UserAppPreferenceChartColors;
    private preferences: PreferencesModel;
    private hasTiktok: boolean = false;
    private timezone: string;


    constructor(
        private localizationService: LocalizationService,
        public journodexSearchService: JournodexRegionsService,
        public journodexApiService: JournodexApiService,
        public prominenceBlockerService: ProminenceLockerService,
        private userPreferencesService: UserPreferencesService,
        private currentUserStateService: CurrentUserStateService,
        private monitorConfigService: MonitorConfigService,
        public currencyConversionService: CurrencyConversionService
    ) {
        this.subscriptions.push(
            this.prominenceBlockerService.prominenceStatus$.subscribe(status => {
                this.prominenceStatus = status;
            }),
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors))
            ).subscribe(userChartColors => {
                this.userChartColors = userChartColors;
            }),
            this.monitorConfigService.getAvailableMediaTypes().subscribe(mediaTypes => {
                this.hasTiktok = mediaTypes.includes(MediaTypeEnum.TIKTOK);
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => this.preferences = prefs)
        );
    }

    /**
     * Responsible for determining and calling the appropriate map function
     */
    public mapFacetValues(series: ReportSeriesModel, response: MentionsSearchResponseModel): Observable<DataPointModel[]> {
        let dataPoints: Observable<DataPointModel[]> = this.prepareDataPointsByChartType(series, response);
        dataPoints = this.prepareDataPointsByVisualization(dataPoints, series.chartSpecificType);
        // if (!response) {
        //     throwError(() => new Error('Data failed to load'));
        // }


        const chartType: ReportBlockTypeEnum = series.chartType;
        // here we convert AVE value to user pref currency
        // top term chart, coverage summary chart and coverage trend chart are not handeld here, they are handled in mapCoverageSummary() and mapTopTerms()
        if (chartType !== ReportBlockTypeEnum.COVERAGE_SUMMARY && chartType !== ReportBlockTypeEnum.TOP_TERMS &&
            chartType !== ReportBlockTypeEnum.COVERAGE_OVER_TIME && series.metric === ChartMetricsEnum.ave) {
            let reportCurrency: UserCurrency;
            reportCurrency = series.styles.currency;
            if (!reportCurrency){
                reportCurrency = this.preferences.currency;
            }
            return dataPoints.pipe(
                tap( points => {
                    points.forEach(v => {
                        if (chartType === ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON) {
                            v.yValue = +this.currencyConversionService.convertCurrency(v.yValue, false, reportCurrency);
                        } else if (!v.currency || v.currency === UserCurrency.USD) {
                            if (v.xValue){
                                v.xValue = +this.currencyConversionService.convertCurrency(v.xValue, false, reportCurrency);
                            }
                        }
                    });
                }));
        }

        return dataPoints;
    }

    // Based on series.chartType to map mention search response to data points
    prepareDataPointsByChartType(series: ReportSeriesModel, response: MentionsSearchResponseModel): Observable<DataPointModel[]>{
        let dataPoints: Observable<DataPointModel[]>;
        const chartType: ReportBlockTypeEnum = series.chartType;

        switch (chartType) {
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
                dataPoints = this.mapMediaTypeBreakdown(series, response);
                break;
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
                dataPoints = this.mapSentimentBreakdown(series, response, series.sentiments);
                break;
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                dataPoints = this.mapSentimentTrend(series, response, series.sentiments);
                break;
            case ReportBlockTypeEnum.TOP_10_OUTLETS: {
                const metric: ChartMetricsEnum = series.metric;
                let facetField: MentionSearchFacetTypesEnum;

                switch (metric) {
                    case ChartMetricsEnum.volumeOfMentions:
                        facetField = MentionSearchFacetTypesEnum.outlet;
                        break;
                    case ChartMetricsEnum.IMPRESSIONS:
                    case ChartMetricsEnum.audienceReach:
                        facetField = MentionSearchFacetTypesEnum.outletByAudienceReach;
                        break;
                    case ChartMetricsEnum.ave:
                        facetField = MentionSearchFacetTypesEnum.outletByAVE;
                        break;
                    case ChartMetricsEnum.domainAuthority:
                        facetField = MentionSearchFacetTypesEnum.outletByDomainAuthority;
                        break;
                    default:
                        facetField = MentionSearchFacetTypesEnum.outlet;
                        break;
                }

                dataPoints = this.mapTop10FacetFieldByVolume(series, response, facetField);
            } break;

            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME: {
                const metric: ChartMetricsEnum = series.metric;
                let facetField: MentionSearchFacetTypesEnum;

                switch (metric) {
                    case ChartMetricsEnum.volumeOfMentions:
                        facetField = MentionSearchFacetTypesEnum.contact;
                        break;
                    case ChartMetricsEnum.IMPRESSIONS:
                    case ChartMetricsEnum.audienceReach:
                        facetField = MentionSearchFacetTypesEnum.authorByAudienceReach;
                        break;
                    case ChartMetricsEnum.ave:
                        facetField = MentionSearchFacetTypesEnum.authorByAVE;
                        break;
                    default:
                        facetField = MentionSearchFacetTypesEnum.contact;
                        break;
                }

                dataPoints = this.mapTop10FacetFieldByVolume(series, response, facetField);
            } break;

            case ReportBlockTypeEnum.TOP_HASHTAGS:
                dataPoints = this.mapTop10FacetFieldByVolume(series, response, MentionSearchFacetTypesEnum.hashtags);
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                // @TODO: remove this and only use mapNeoFacetValues after saved mention search supports neofacets
                if (response && !response.neoFacetResults?.[0]?.buckets) {
                    dataPoints = this.mapDateFacetValues(series, response, ReportBlockTypeEnum.COVERAGE_OVER_TIME);
                } else {
                    dataPoints = this.mapNeoFacetValues(series, response, ReportBlockTypeEnum.COVERAGE_OVER_TIME);
                }
                break;
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                dataPoints = this.mapGeographyBreakdownByVolume(response, series);
                break;
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
                dataPoints = this.mapShareOfVoiceByVolumeOfMentions(response, series);
                break;
            case ReportBlockTypeEnum.COVERAGE_SUMMARY:
                dataPoints = this.mapCoverageSummary(response, series);
                break;
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                dataPoints = this.mapDateFacetValues(series, response, ReportBlockTypeEnum.COVERAGE_OVER_TIME);
                break;
            case ReportBlockTypeEnum.PROMINENCE_OVER_TIME:
                dataPoints = this.mapDateFacetValues(series, response, ReportBlockTypeEnum.PROMINENCE_OVER_TIME);
                break;
            case ReportBlockTypeEnum.PROMINENCE_BY_REACH:
                dataPoints = this.mapCoverageProminence(series, response);
                break;
            case ReportBlockTypeEnum.RECENT_TOPICS:
                dataPoints = this.mapDateFacetValues(series, response, ReportBlockTypeEnum.RECENT_TOPICS);
                break;
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
                dataPoints = this.mapSocialCoverageByNetwork(series, response);
                break;
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                dataPoints = this.mapCoverageComparison(series, response);
                break;
            case ReportBlockTypeEnum.TOP_TERMS:
                dataPoints = this.mapTopTerms(series, response);
                break;

        }

        return dataPoints;
    }

    // Based on visualization type (bar, line) to further prepare data points for different visualization
    prepareDataPointsByVisualization(dataPoints: Observable<DataPointModel[]>, chartSpecificType: ChartSpecificTypesEnum): Observable<DataPointModel[]> {
        if (!dataPoints || !chartSpecificType) {
            return dataPoints;
        }

        return dataPoints.pipe(tap(data => {
            switch (chartSpecificType) {
                case ChartSpecificTypesEnum.LINE:
                    // add code when we update each chart
                    break;
            }
        }));
    }

    /**
     * Maps facet data to media type DataPointModel[]
     */
    private mapMediaTypeBreakdown(series: ReportSeriesModel, response: MentionsSearchResponseModel): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];

        if (response && response.facetResults) {
            for (const facetType of response.facetResults) {
                if (facetType.field !== MentionSearchFacetTypesEnum.mediaType) {
                    continue;
                }

                const mediaTypeBreakdownDataMap: Map<MediaTypeEnum, DataPointModel> = new Map();

                for (const facetValue of facetType.values) {
                    let dataPoint: DataPointModel;
                    const mediaType = facetValue.value as MediaTypeEnum;

                    if (!!series.styles.networkSelection && !series.styles.networkSelection.includes(mediaType)) {
                        continue;
                    }

                    if (mediaType === MediaTypeEnum.TIKTOK && !this.hasTiktok) {
                        continue;
                    }

                    const mapping = mediaTypeToPieMap.get(mediaType);

                    if (mapping) {
                        const s = series.clone();
                        s.mentions.combinedQuery.includedMediaTypes = [ new MediaTypeModel(mediaType) ];

                        dataPoint = {
                            label: this.localizationService.transform(mapping.label),
                            id: facetValue.value,
                            xValue: facetValue.count,
                            color: this.userChartColors.mediaTypeColors[mediaTypeColorKeyMap.get(mediaType)],
                            labelColor: mapping.labelColor,
                            percent: parseFloat((facetValue.count / response.totalResults * 100).toFixed(1)),
                            viewUrl: s.getMentionSeriesUrl()
                        };

                        mediaTypeBreakdownDataMap.set(mediaType, dataPoint);
                    }
                }

                // This is so that they always appear in the order specified by the MEDIA_TYPE_MAP
                for (const [key, value] of Array.from(mediaTypeToPieMap)) {
                    if (mediaTypeBreakdownDataMap.get(key)) {
                        data.push(mediaTypeBreakdownDataMap.get(key));
                    }
                }
            }
        }


        return of(data);
    }

    /**
     * Maps facet data to sentiment DataPointModel[]
     */
    private mapSentimentBreakdown(
        series: ReportSeriesModel,
        response: MentionsSearchResponseModel,
        sentiments: SearchCriteriaQuerySentimentsEnum[]
    ): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];
        const s = series.clone();

        s.mentions.combinedQuery.sentiments = [ new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive) ];
        const positiveDataPoint: DataPointModel = {
            label: this.localizationService.transform(`Sentiment.${SearchCriteriaQuerySentimentsEnum.positive}`),
            xValue: 0,
            color: this.userChartColors.sentimentColors.positive,
            labelColor: chartColors.agprWhite,
            id: SearchCriteriaQuerySentimentsEnum.positive,
            percent: null,
            viewUrl: s.getMentionSeriesUrl()
        };


        s.mentions.combinedQuery.sentiments = [ new SentimentModel(SearchCriteriaQuerySentimentsEnum.neutral) ];
        const neutralDataPoint: DataPointModel = {
            label: this.localizationService.transform(`Sentiment.${SearchCriteriaQuerySentimentsEnum.neutral}`),
            xValue: 0,
            color: this.userChartColors.sentimentColors.neutral,
            labelColor: chartColors.agprBlack,
            id: SearchCriteriaQuerySentimentsEnum.neutral,
            percent: null,
            viewUrl: s.getMentionSeriesUrl()
        };

        s.mentions.combinedQuery.sentiments = [ new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative) ];
        const negativeDataPoint: DataPointModel = {
            label: this.localizationService.transform(`Sentiment.${SearchCriteriaQuerySentimentsEnum.negative}`),
            xValue: 0,
            color: this.userChartColors.sentimentColors.negative,
            labelColor: chartColors.agprWhite,
            id: SearchCriteriaQuerySentimentsEnum.negative,
            percent: null,
            viewUrl: s.getMentionSeriesUrl()
        };

        s.mentions.combinedQuery.sentiments = [ new SentimentModel(SearchCriteriaQuerySentimentsEnum.notAvailable) ];
        const notAvailableDataPoint: DataPointModel = {
            label: this.localizationService.transform(`Sentiment.${SearchCriteriaQuerySentimentsEnum.notAvailable}`),
            xValue: 0,
            color: this.userChartColors.sentimentColors.notAvailable,
            labelColor: chartColors.agprBlack,
            id: SearchCriteriaQuerySentimentsEnum.notAvailable,
            percent: null,
            viewUrl: s.getMentionSeriesUrl()
        };
        s.mentions.combinedQuery.sentiments = [];

        if (response && response.facetResults) {
            const sentimentResults = this.getSentimentValuesFromResponse(response.facetResults);
            positiveDataPoint.xValue = sentimentResults.positive;
            neutralDataPoint.xValue = sentimentResults.neutral;
            negativeDataPoint.xValue = sentimentResults.negative;
            notAvailableDataPoint.xValue = sentimentResults.notAvailable;

            if (series.chartType === ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON) {
                positiveDataPoint.value0 = sentimentResults.positive;
                neutralDataPoint.value1 = sentimentResults.neutral;
                negativeDataPoint.value2 = sentimentResults.negative;
                notAvailableDataPoint.value3 = sentimentResults.notAvailable;
            }

            const totalMentions = response.totalResults;
            if (totalMentions > 0) {
                const positivePercent = parseFloat((safeDivide(positiveDataPoint.xValue, totalMentions) * 100).toFixed(1));
                const neutralPercent = parseFloat((safeDivide(neutralDataPoint.xValue, totalMentions) * 100).toFixed(1));
                const negativePercent = parseFloat((safeDivide(negativeDataPoint.xValue, totalMentions) * 100).toFixed(1));
                const notAvailablePercent = parseFloat((safeDivide(notAvailableDataPoint.xValue, totalMentions) * 100).toFixed(1));
                positiveDataPoint.percent = positivePercent;
                neutralDataPoint.percent = neutralPercent;
                negativeDataPoint.percent = negativePercent;
                notAvailableDataPoint.percent = notAvailablePercent;
                if (series.chartType === ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON) {
                    positiveDataPoint.value0_percent = positivePercent;
                    neutralDataPoint.value1_percent = neutralPercent;
                    negativeDataPoint.value2_percent = negativePercent;
                    notAvailableDataPoint.value3_percent = notAvailablePercent;
                }
            }
        }

        // Order matters here, this is the order they'll be displayed in the legend
        if (sentiments.includes(SearchCriteriaQuerySentimentsEnum.positive)) {
            data.push(positiveDataPoint);
        }

        if (sentiments.includes(SearchCriteriaQuerySentimentsEnum.neutral)) {
            data.push(neutralDataPoint);
        }

        if (sentiments.includes(SearchCriteriaQuerySentimentsEnum.negative)) {
            data.push(negativeDataPoint);
        }

        if (sentiments.includes(SearchCriteriaQuerySentimentsEnum.notAvailable)) {
            data.push(notAvailableDataPoint);
        }

        if (data.length < 1) {
            return of(null);
        }
        return of(data);
    }

    /**
     * Maps facet data to sentiment trend DataPointModel[]
     */
    private mapSentimentTrend(
        series: ReportSeriesModel,
        response: MentionsSearchResponseModel,
        sentiments: SearchCriteriaQuerySentimentsEnum[]
    ): Observable<DataPointModel[]> {
        const neoFacet = response.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.sentimentOverTime);
        if (!neoFacet) {
            return of([]);
        }

        const mentionUrlsConfig = {
            [`${SearchCriteriaQuerySentimentsEnum.positive}`]: {
                mentionFilters: new MentionSearchCriteriaModel(),
                viewMentionUrl: ''
            },
            [`${SearchCriteriaQuerySentimentsEnum.neutral}`]: {
                mentionFilters: new MentionSearchCriteriaModel(),
                viewMentionUrl: ''
            },
            [`${SearchCriteriaQuerySentimentsEnum.notAvailable}`]: {
                mentionFilters: new MentionSearchCriteriaModel(),
                viewMentionUrl: ''
            },
            [`${SearchCriteriaQuerySentimentsEnum.negative}`]: {
                mentionFilters: new MentionSearchCriteriaModel(),
                viewMentionUrl: ''
            },
        };

        Object.entries(mentionUrlsConfig).forEach(([sentiment, config], index) => {
            mentionUrlsConfig[sentiment].mentionFilters.query.sentiments = [
                new SentimentModel(sentiment as SearchCriteriaQuerySentimentsEnum)
            ];
        });

        return of(neoFacet?.buckets.map(
            bucket => {
                const positiveSum = (bucket.buckets[ReportsNeoFacetKeys.sentimentTrend] as BucketResponseModel[]).reduce(
                    (previousValue, bucketValue) => {
                        return +bucketValue.termValue >= 0.3 ? previousValue + bucketValue.count : previousValue;
                    }, 0
                );

                const neutralSum = (bucket.buckets[ReportsNeoFacetKeys.sentimentTrend] as BucketResponseModel[]).reduce(
                    (previousValue, bucketValue) => {
                        return +bucketValue.termValue < 0.3 && +bucketValue.termValue > -0.3 ? previousValue + bucketValue.count : previousValue;
                    }, 0
                );
                const negativeSum = (bucket.buckets[ReportsNeoFacetKeys.sentimentTrend] as BucketResponseModel[]).reduce(
                    (previousValue, bucketValue) => {
                        return +bucketValue.termValue <= -0.3 ? previousValue + bucketValue.count : previousValue;
                    }, 0
                );

                return <DataPointModel>{
                    xValue: bucket.intervalValue.start,
                    [`yValue_${SearchCriteriaQuerySentimentsEnum.positive}`]: positiveSum,
                    [`mentionFilters_${SearchCriteriaQuerySentimentsEnum.positive}`]:
                    JSON.stringify(mentionUrlsConfig[SearchCriteriaQuerySentimentsEnum.positive].mentionFilters.getApiEntity()),
                    [`yValue_${SearchCriteriaQuerySentimentsEnum.neutral}`]: neutralSum,
                    [`mentionFilters_${SearchCriteriaQuerySentimentsEnum.neutral}`]:
                    JSON.stringify(mentionUrlsConfig[SearchCriteriaQuerySentimentsEnum.neutral].mentionFilters.getApiEntity()),
                    [`yValue_${SearchCriteriaQuerySentimentsEnum.notAvailable}`]: bucket.count - positiveSum - negativeSum - neutralSum,
                    [`mentionFilters_${SearchCriteriaQuerySentimentsEnum.notAvailable}`]:
                    JSON.stringify(mentionUrlsConfig[SearchCriteriaQuerySentimentsEnum.notAvailable].mentionFilters.getApiEntity()),
                    [`yValue_${SearchCriteriaQuerySentimentsEnum.negative}`]: negativeSum,
                    [`mentionFilters_${SearchCriteriaQuerySentimentsEnum.negative}`]:
                    JSON.stringify(mentionUrlsConfig[SearchCriteriaQuerySentimentsEnum.negative].mentionFilters.getApiEntity())
                };
            }
        ));
    }

    private mapTop10FacetFieldByVolume(
        series: ReportSeriesModel,
        response: MentionsSearchResponseModel,
        facetField: MentionSearchFacetTypesEnum
    ): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];

        if (response && response.facetResults) {
            const facetResults: FacetResponseModel = response.facetResults.find(facetType => {
                return facetType.field === facetField;
            });

            if (!facetResults) {
                console.error('no facet results for facet: ', facetField);
                return of(data);
            }

            let totalResults;
            if (this.isValueSum(facetField)) {

                // ave uses the 'sum' field instead of the count field
                totalResults = facetResults.values.reduce((acc, curr) => acc + curr.sum, 0);
            } else {
                totalResults = facetResults.values.reduce((acc, curr) => acc + curr.count, 0);
            }

            for (const facetValue of facetResults.values) {
                if (!facetValue.value || facetValue.value.trim() === '') {
                    // Ignore facets with empty labels
                    continue;
                }

                let percent;
                if (this.isValueSum(facetField)) {
                    percent = parseFloat((facetValue.sum / totalResults * 100).toFixed(1));

                } else {
                    percent = parseFloat((facetValue.count / totalResults * 100).toFixed(1));
                }

                const s = series.clone();

                switch (facetField) {
                    case MentionSearchFacetTypesEnum.contact:
                    case MentionSearchFacetTypesEnum.authorByAVE:
                    case MentionSearchFacetTypesEnum.authorByAudienceReach:
                        const author = new JournodexSourceModel();
                        author.entityType = SearchCriteriaSourcesEnum.journalist;
                        author.entityName = facetValue.value;
                        if (!s.mentions.combinedQuery) {
                            s.mentions.combinedQuery = new MentionSearchQueryModel();
                        }
                        s.mentions.combinedQuery.includedSources = [ author ];
                        break;
                    case MentionSearchFacetTypesEnum.outlet:
                    case MentionSearchFacetTypesEnum.outletByAVE:
                    case MentionSearchFacetTypesEnum.outletByAudienceReach:
                    case MentionSearchFacetTypesEnum.outletByDomainAuthority:
                        const outlet = new JournodexSourceModel();
                        outlet.entityType = SearchCriteriaSourcesEnum.outlet;
                        outlet.entityName = facetValue.value;
                        if (s.mentions?.combinedQuery) {
                            s.mentions.combinedQuery.includedSources = [ outlet ];
                        }
                        break;
                    case MentionSearchFacetTypesEnum.hashtags:
                        s.mentions.combinedQuery.hashtags.include = [ facetValue.value ];
                        break;
                }

                const count = this.mapXValueByFacet(facetValue, facetField);
                const dataPoint: DataPointModel = {
                    label: facetValue.value.replace(/[\r\n]+/g, ' '),
                    xValue: count,
                    percent: percent,

                    // for identifying source on chart segment click later
                    viewUrl: s.getMentionSeriesUrl()
                };
                dataPoint['value0'] = count;
                dataPoint['value0_percent'] = percent;
                if (dataPoint.xValue && dataPoint.xValue !== 0) {
                    data.push(dataPoint);
                }
            }
        }

        return of(data);
    }

    mapGeographyBreakdownByVolume(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        const dataPointsResponse: BehaviorSubject<DataPointModel[]> =  new BehaviorSubject<DataPointModel[]>(null);
        const data: DataPointModel[] = [];

        if (
            series
            && series.styles
            && series.styles.geographyScale === GeographyTypesEnum.country
        ) {
            const countryJournodexId = +this.journodexSearchService.getCountryByIsoCode(series.styles.regionIsoCode + '').id;
            const request = this.getCountrySubdivisions(countryJournodexId, <GeographyTypesEnum>series.styles.geographyScale);

            request.subscribe(provinces => {
                if (response && response.facetResults) {
                    const total = response.totalResults;
                    let count = 0;

                    for (const facetType of response.facetResults) {
                        if (facetType.field !== MentionSearchFacetTypesEnum.province) {
                            continue;
                        }

                        for (const facetValue of facetType.values) {
                            count += facetValue.count;
                            const region = provinces.find(province => (province.province.id === +facetValue.value));
                            if (!region) {
                                continue;
                            }

                            let viewUrl;
                            let filterErrorMessage;
                            // HACK some region filters cannot be safely applied to coverage by region chart
                            try {
                                const s = series.clone();
                                s.mentions.combinedQuery.includedRegions = [ region ];
                                viewUrl = s.getMentionSeriesUrl();
                            } catch (error) {
                                filterErrorMessage = this.generateFilterErrorMessage(error);
                            }

                            data.push({
                                id: `${series.styles.regionIsoCode}-${region.isoCode}`,
                                label: region.province.name,
                                xValue: facetValue.count,
                                // for identifying source on chart segment click later
                                viewUrl,
                                filterErrorMessage
                            });
                        }

                        // Country based chart, for mentions that do not have province info, we set its id to null
                        // this will be added to 'other' legend in map chart
                        if (count < total) {
                            data.push({
                                id: null,
                                xValue: total - count,
                            });
                        }

                        data.forEach((dataPoint) => {
                            dataPoint.yValue = parseFloat((dataPoint.xValue / total * 100).toFixed(1));
                        });
                    }
                }

                dataPointsResponse.next(data);
            });
        } else {
            if (response && response.facetResults) {
                let total = 0;
                for (const facetType of response.facetResults) {
                    if (facetType.field !== MentionSearchFacetTypesEnum.country) {
                        continue;
                    }

                    for (const facetValue of facetType.values) {
                        total += facetValue.count;
                        const country = this.journodexSearchService.getCountryById(facetValue.value);

                        if (!country) {
                            continue;
                        }
                        const geoModel = new JournodexGeographyModel();
                        geoModel.geographicType = GeographyTypesEnum.country;
                        geoModel.id = +country.id;


                        let viewUrl;
                        let filterErrorMessage;
                        // HACK some region filters cannot be safely applied to coverage by region chart
                        try {
                            const s = series.clone();
                            s.mentions.combinedQuery.includedRegions = [ geoModel ];
                            viewUrl = s.getMentionSeriesUrl();
                        } catch (error) {
                            filterErrorMessage = this.generateFilterErrorMessage(error);
                        }

                        data.push({
                            id:  country.countryCode,
                            label: country.name,
                            xValue: facetValue.count,
                            // for identifying source on chart segment click later
                            viewUrl,
                            filterErrorMessage
                        });
                    }

                    data.forEach((dataPoint) => {
                        dataPoint.yValue = parseFloat((dataPoint.xValue / total * 100).toFixed(1));
                    });
                }
            }

            dataPointsResponse.next(data);
        }

        return dataPointsResponse;
    }

    generateFilterErrorMessage(error: any): string {
        if (error?.filterTypes?.some( errorType =>
            errorType === monitoringFiltersEnum.regions
            || errorType === monitoringFiltersEnum.country
            || errorType === monitoringFiltersEnum.city
            || errorType === monitoringFiltersEnum.province
        )) {
            return this.localizationService.transform('Reports.filterError.regions');
        } else {
            throw error;
        }
    }

    /**
     * Get Country subdivisions
     */
    getCountrySubdivisions(journodexId: number, geographyScale: GeographyTypesEnum): Observable<JournodexGeographyModel[]> {
        const geographyModel = new JournodexGeographyModel();
        geographyModel.id = journodexId;
        geographyModel.geographicType = geographyScale;
        geographyModel.country = <JournodexGeographicEntityModel>{ id: journodexId, name: null, names: null };

        return this.journodexSearchService.getChildRegions(geographyModel);
    }

    /**
     * Prepare data for coverage over time charting
     */
    private mapDateFacetValues(
        series: ReportSeriesModel,
        response: MentionsSearchResponseModel,
        type: ReportBlockTypeEnum
    ): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];
        if (response &&
            response.dateFacetResults &&
            response.dateFacetResults[0] &&
            response.dateFacetResults[0].values
        ) {
            for (const facet of response.dateFacetResults[0].values) {
                const dateFacet = {
                    aggregateFacets: facet.aggregateFacets,
                    count: facet.count,
                    from: facet.from,
                    to: facet.to,
                    prominenceAverages: facet.prominenceAverages
                } as DateFacetValueModel;
                if (this.preferences && this.preferences.timeZone || this.timezone) {
                    dateFacet.from = moment(moment(dateFacet.from).tz(this.preferences.timeZone || this.timezone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                    dateFacet.to = moment(moment(dateFacet.to).tz(this.preferences.timeZone || this.timezone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                }

                const scale = getTimeScale(dateFacet.from, dateFacet.to);
                const date = moment(dateFacet.from).startOf(scale).toDate();

                const filterOverrides = new MentionSearchCriteriaModel();
                filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                filterOverrides.query.dateQuery.absoluteRange = {
                    start: new Date(facet.from),
                    end: new Date(facet.to)
                };

                // HACK : prevent data from going into the chart if we recalculation is under way
                // But in reality we should instead prevent the series service from fetching it at all
                // This can be achieved when the slow/fast facet system gets replaced.
                if (this.prominenceStatus !== ProminenceRecalculationJobStatus.DONE &&
                    (type === ReportBlockTypeEnum.PROMINENCE_OVER_TIME || type === ReportBlockTypeEnum.PROMINENCE_BY_REACH)) {
                    continue;
                }

                const dataPoint = {
                    yValue: this.getFacetFieldFromDateFacet(dateFacet, type, series.prominenceTrendGroup?.id, series.metric),
                    xValue: date.getTime(),
                    xValueStartTime: moment(dateFacet.from).startOf(scale).format('YYYY/MM/DD HH:mm'),
                    xValueEndTime: moment(dateFacet.to).startOf(scale).format('YYYY/MM/DD HH:mm'),
                    // display hour:second or date based on scale
                    label: scale === 'hour' ? date.toTimeString().substring(0, 5) : date.toDateString(),
                    startTime: moment(dateFacet.from).format('YYYY/MM/DD HH:mm'),
                    endTime: moment(dateFacet.to).format('YYYY/MM/DD HH:mm'),
                    rawDateFacetFrom: facet.from,
                    rawDateFacetTo: facet.to,
                    // for prominence over time chart, yValue is average prominence instead of mentions and we want to display mention count
                    // thus we also pass dateFacet.count
                    mentionCount: dateFacet.count,
                    // for identifying source on chart segment click later
                    viewUrl: series.getMentionSeriesUrl(filterOverrides)
                };
                if (series.metrics?.length > 0) {
                    series.metrics.forEach((metric, index) => {
                        dataPoint[`yValue_${metric}`] = this.getFacetFieldFromDateFacet(dateFacet, type, series.prominenceTrendGroup?.id, metric);
                        dataPoint[`yValue${index}`] = this.getFacetFieldFromDateFacet(dateFacet, type, series.prominenceTrendGroup?.id, metric);
                    });
                    // convert ave currency
                    if (dataPoint['yValue_AVE']){
                        dataPoint['yValue_AVE'] = +this.currencyConversionService.convertCurrency(dataPoint['yValue_AVE'] as number,
                            false, series?.styles?.currency ?? this.preferences.currency);
                    }
                }
                data.push(dataPoint);
            }

        }
        return of(data);
    }

    mapNeoFacetValues(
        series: ReportSeriesModel,
        response: MentionsSearchResponseModel,
        type: ReportBlockTypeEnum
    ): Observable<DataPointModel[]> {
        const neoFacet = response?.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
        if (!neoFacet) {
            return of([]);
        }
        return of(neoFacet?.buckets.map(
            bucket => {
                // amcharts only work with local timezone, so start and end time needs to change to user preference timezone (timestamp changed)
                let startTime = bucket.intervalValue.start;
                let endTime = bucket.intervalValue.end;
                if (this.preferences?.timeZone || this.timezone) {
                    startTime = moment(moment(startTime)
                        .tz(this.preferences.timeZone || this.timezone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                    endTime = moment(moment(endTime)
                        .tz(this.preferences.timeZone || this.timezone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                }

                const timeScale = getTimeScale(startTime, endTime);
                const date = moment(startTime).startOf(timeScale).toDate();

                const filterOverrides = new MentionSearchCriteriaModel();
                filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                filterOverrides.query.dateQuery.dateType = series.dateRange?.dateType;
                // for generating the filters we should use the original start and end time
                filterOverrides.query.dateQuery.absoluteRange = {
                    start: new Date(bucket.intervalValue.start),
                    end: new Date(bucket.intervalValue.end)
                };
                const data = {
                    yValue: this.getFacetFieldFromNeoFacet(bucket, series.metric),
                    xValue: date.getTime(),
                    xValueStartTime: moment(startTime).startOf(timeScale).format('YYYY/MM/DD HH:mm'),
                    xValueEndTime: moment(endTime).startOf(timeScale).format('YYYY/MM/DD HH:mm'),
                    label: timeScale === 'hour' ? date.toTimeString().substring(0, 5) : date.toDateString(),
                    startTime: moment(startTime).format('YYYY/MM/DD HH:mm'),
                    endTime: moment(endTime).format('YYYY/MM/DD HH:mm'),
                    rawDateFacetFrom: bucket.intervalValue.start,
                    rawDateFacetTo: bucket.intervalValue.end,
                    mentionCount: bucket.count,
                    viewUrl: series.getMentionSeriesUrl(filterOverrides),

                };
                if (series.metrics?.length > 0) {
                    let aveIndex = -1;
                    series.metrics.forEach((metric, index) => {
                        data[`yValue_${metric}`] = this.getFacetFieldFromNeoFacet(bucket, metric);
                        data[`yValue${index}`] = this.getFacetFieldFromNeoFacet(bucket, metric);
                        if (metric === ChartMetricsEnum.ave) {
                            aveIndex = index;
                        }
                    });
                    // convert ave currency
                    if (data['yValue_AVE']){
                        data['yValue_AVE'] = +this.currencyConversionService.convertCurrency(data['yValue_AVE'] as number, false,
                            series?.styles?.currency ?? this.preferences.currency);
                    }
                    if (aveIndex > -1) {
                        data[`yValue${aveIndex}`] = +this.currencyConversionService.convertCurrency(data[`yValue${aveIndex}`] as number, false,
                            series?.styles?.currency ?? this.preferences.currency);
                    }
                }
                return data;
            }
        ));
    }

    /**
     * Prepare data for shareOfVoiceByVolumeOfMentions
     */
    private mapShareOfVoiceByVolumeOfMentions(
        response: MentionsSearchResponseModel,
        series: ReportSeriesModel
    ): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];
        let totalResults = response?.totalResults ?? 0;

        if (response) {
            if (series.metric) {
                if ((series.metric === ChartMetricsEnum.ave) ||
                    (series.metric === ChartMetricsEnum.audienceReach) || (series.metric === ChartMetricsEnum.IMPRESSIONS)
                ) {
                    if (response.dateFacetResults && response.dateFacetResults[0]) {
                        if (series.metric === ChartMetricsEnum.ave) {
                            totalResults = 0;
                            for (const dateFacetResult of response.dateFacetResults[0].values) {
                                totalResults += (dateFacetResult.aggregateFacets?.SUM_AD_VALUE_USD || 0);
                            }
                        } else if ((series.metric === ChartMetricsEnum.audienceReach) || (series.metric === ChartMetricsEnum.IMPRESSIONS)) {
                            totalResults = 0;
                            for (const dateFacetResult of response.dateFacetResults[0].values) {
                                totalResults += dateFacetResult.aggregateFacets?.SUM_AUDIENCE_REACH;
                            }
                        }
                    }
                } else if (series.metric === ChartMetricsEnum.ENGAGEMENTS) {
                    totalResults = 0;
                    const neoFacetField = ReportsNeoFacetKeys.totalEngagements;
                    const neoFacetResults: NeoFacetResponseModel = response.neoFacetResults?.find(neofacetType => {
                        return neofacetType.key === neoFacetField;
                    });

                    if (neoFacetResults) {
                        for (const neoFacetValue of neoFacetResults.buckets) {
                            totalResults += this.getMetricValue(neoFacetValue, series.metric);
                        }
                    }
                }
            }

            if (series.mentions.isPreviousPeriod && series.dateRange.type === DateQueryTypeEnum.ALL) {
                totalResults = 0;
            }
        }

        data.push({
            label: series.label,
            xValue: totalResults,
            color: series.color,
            // for identifying source on chart segment click later
            viewUrl: series.getMentionSeriesUrl(null, series.mentions.isPreviousPeriod),
            seriesId: series.id
        });

        return of(data);
    }

    /**
     * Prepare data for coverage prominence
     */
    private mapCoverageProminence(
        series: ReportSeriesModel,
        response: MentionsSearchResponseModel
    ): Observable<DataPointModel[]> {
        // Default value of thresholds
        //  reachThreshold: 150000
        //  prominenceThreshold: 0.69

        // Leader Filters (above both thresholds)
        const leaderFilters = new MentionSearchCriteriaModel();
        leaderFilters.query.audienceReach = {
            min: 150000,
            max: 999999999999
        };
        leaderFilters.query.dateQuery = null;
        // The purpose of dividing range is showing each prominence filter chip
        leaderFilters.query.includedProminenceFilter = [
            {
                prominenceGroup: series.prominenceTrendGroup,
                ranges: [
                    {
                        min: 0.69,
                        max: 0.99
                    },
                    {
                        min: 1,
                        max: 1
                    }]
            }
        ];
        // Prevalent Filters (above reach threshold and below prominence threshold)
        const prevalentFilters = new MentionSearchCriteriaModel();
        prevalentFilters.query.audienceReach = {
            min: 150000,
            max: 999999999999
        };
        prevalentFilters.query.dateQuery = null;

        // The purpose of dividing range is showing each prominence filter chip
        prevalentFilters.query.includedProminenceFilter = [
            {
                prominenceGroup: series.prominenceTrendGroup,
                ranges: [
                    {
                        min: 0,
                        max: 0
                    },
                    {
                        min: 0.1,
                        max: 0.29
                    },
                    {
                        min: 0.30,
                        max: 0.43
                    },
                    {
                        min: 0.44,
                        max: 0.69
                    }
                ]
            }
        ];

        // Notable Filters (above prominence threshold and below reach threshold)
        const notableFilters = new MentionSearchCriteriaModel();
        notableFilters.query.dateQuery = null;

        // Setting min value to 0 will cause mention count mismatch when viewing mention from the link vs the chart
        // The chart in the report includes n/a values but the search of View Mentions link in the chart
        // does not include n/a if there is an audience reach filter, so setting min to null
        notableFilters.query.audienceReach = {
            min: null,
            max: 150000
        };
        // The purpose of dividing range is showing each prominence filter chip
        notableFilters.query.includedProminenceFilter = [
            {
                prominenceGroup: series.prominenceTrendGroup,
                ranges: [
                    {
                        min: 0.70,
                        max: 0.99
                    },
                    {
                        min: 1,
                        max: 1
                    }]
            }
        ];
        // Niche Filters (below both thresholds)
        const nicheFilters = new MentionSearchCriteriaModel();

        // Setting min value to 0 will cause mention count mismatch when viewing mention from the link vs the chart
        // The chart in the report includes n/a values but the search of View Mentions link in the chart
        // does not include n/a if there is an audience reach filter, so setting min to null
        nicheFilters.query.audienceReach = {
            min: null,
            max: 150000
        };
        nicheFilters.query.dateQuery = null;
        // The purpose of dividing range is showing each prominence filter chip
        nicheFilters.query.includedProminenceFilter = [
            {
                prominenceGroup: series.prominenceTrendGroup,
                ranges: [
                    {
                        min: 0,
                        max: 0
                    },
                    {
                        min: 0.1,
                        max: 0.29
                    },
                    {
                        min: 0.30,
                        max: 0.43
                    },
                    {
                        min: 0.44,
                        max: 0.69
                    }
                ]
            }
        ];

        const dataPoints = [
            // the order of points influences the position at which they show up in the chart despite xValue and yValue being specified
            // count and percent will be set based on response later
            {
                xValue: 1,
                yValue: 1,
                label: this.localizationService.transform('Reports.prominenceByReach.leader'),
                percent: 0,
                count: 0,
                color: series.color,
                viewUrl: series.getMentionSeriesUrl(leaderFilters)
            },
            {
                xValue: 1,
                yValue: 0,
                label: this.localizationService.transform('Reports.prominenceByReach.prevalent'),
                percent: 0,
                count: 0,
                color: series.color,
                viewUrl: series.getMentionSeriesUrl(prevalentFilters)
            },
            {
                xValue: 0,
                yValue: 1,
                label: this.localizationService.transform('Reports.prominenceByReach.notable'),
                percent: 0,
                count: 0,
                color: series.color,
                viewUrl: series.getMentionSeriesUrl(notableFilters)
            },
            {
                xValue: 0,
                yValue: 0,
                label: this.localizationService.transform('Reports.prominenceByReach.niche'),
                percent: 0,
                count: 0,
                color: series.color,
                viewUrl: series.getMentionSeriesUrl(nicheFilters)
            }
        ];

        const resHeatMap = response.prominenceByReachResult?.heatMap;
        if (resHeatMap) {
            dataPoints.forEach(dataPoint => {
                resHeatMap.forEach(resPoint => {
                    if (resPoint.x === dataPoint.xValue && resPoint.y === dataPoint.yValue) {
                        dataPoint.count = resPoint.count;
                        dataPoint.percent = resPoint.percent;
                    }
                });
            });
        }

        return of(dataPoints);
    }

    private getFacetFieldFromDateFacet(
        dateFacet: DateFacetValueModel,
        type: ReportBlockTypeEnum,
        prominenceGroupId?: number,
        analysisMetric?: ChartMetricsEnum
    ): number {
        switch (type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.RECENT_TOPICS:
                if (analysisMetric) {
                    if (analysisMetric === ChartMetricsEnum.ave) {
                        return dateFacet.aggregateFacets?.SUM_AD_VALUE_USD;
                    } else if (analysisMetric === ChartMetricsEnum.IMPRESSIONS) {
                        return dateFacet.aggregateFacets?.SUM_AUDIENCE_REACH;
                    } else if (analysisMetric === ChartMetricsEnum.volumeOfMentions) {
                        return dateFacet.count;
                    }
                    return null;
                }
                return dateFacet.count;

            case ReportBlockTypeEnum.PROMINENCE_OVER_TIME:
                if (dateFacet.prominenceAverages) {
                    if (dateFacet.prominenceAverages[prominenceGroupId]) {
                        // Normalize to 0-5 value to facilitate usage in the chart
                        return Math.round(dateFacet.prominenceAverages[prominenceGroupId] * 5 * 100) / 100;
                    }
                }
                return 0;

            default:
                break;
        }
    }

    private getFacetFieldFromNeoFacet(
        bucket: BucketResponseModel,
        analysisMetric: ChartMetricsEnum
    ): number {
        let res = 0;
        switch (analysisMetric) {
            case ChartMetricsEnum.volumeOfMentions:
                return bucket.count;

            case ChartMetricsEnum.ave:
                if (bucket.buckets[ReportsNeoFacetKeys.aveOverTimeFacet] && bucket.buckets[ReportsNeoFacetKeys.aveOverTimeFacet][0]?.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.aveOverTimeFacet][0].count;
                }
                break;
            case ChartMetricsEnum.IMPRESSIONS:
                if (bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet] && bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet][0]?.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet][0].count;
                }
                break;
            case ChartMetricsEnum.ENGAGEMENTS:
                if (bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet] && bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet][0]?.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet][0].count;
                }
                break;
            case ChartMetricsEnum.ENGAGEMENT_RATE:
                if (bucket.count !== 0
                        && bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet]
                        && bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet][0]?.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet][0].count / bucket.count;
                }
                break;
            case ChartMetricsEnum.APPLAUSE:
                if (bucket.buckets[ReportsNeoFacetKeys.engagementTotalLikes] && bucket.buckets[ReportsNeoFacetKeys.engagementTotalLikes][0]?.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.engagementTotalLikes][0].count;
                }
                break;
            case ChartMetricsEnum.CONVERSATION:
                if (bucket.buckets[ReportsNeoFacetKeys.engagementTotalQuotes] && bucket.buckets[ReportsNeoFacetKeys.engagementTotalQuotes][0]?.count){
                    res += bucket.buckets[ReportsNeoFacetKeys.engagementTotalQuotes][0].count;
                }

                if (bucket.buckets[ReportsNeoFacetKeys.engagementTotalReply] && bucket.buckets[ReportsNeoFacetKeys.engagementTotalReply][0]?.count) {
                    res += bucket.buckets[ReportsNeoFacetKeys.engagementTotalReply][0].count;
                }
                break;
            case ChartMetricsEnum.AMPLIFICATION:
                if (bucket.buckets[ReportsNeoFacetKeys.engagementTotalShare] && bucket.buckets[ReportsNeoFacetKeys.engagementTotalShare][0]?.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.engagementTotalShare][0].count;
                }
                break;
            default:
                return bucket.count;
        }

        return res;
    }

    /**
     * Prepares data for the Coverage Summary table (it's not really a chart)
     */
    public mapCoverageSummary(
        response: MentionsSearchResponseModel,
        series?: ReportSeriesModel
    ): Observable<DataPointModel[]> {
        // Default for backwards compatibility
        let metrics: ChartMetricsEnum[] = [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.audienceReach, ChartMetricsEnum.ave];
        if (series?.metrics?.length) {
            metrics = series.metrics;
        }

        const data: DataPointModel[] = [];

        const mentionsDataPoint =  {
            id: ChartMetricsEnum.volumeOfMentions as string,
            label: this.localizationService.transform('Reports.totalMentions'),
            csvLabel: this.localizationService.transform('Reports.totalMentions.csv'),
            jsonLabel: this.localizationService.transform('Reports.totalMentions.json'),
            xValue: response.totalResults,
            numberFormat: '1.0',
            useSignificantDigits: 1
        };

        let audienceSum = 0;
        let aveSum = 0;

        if (response?.dateFacetResults?.[0]?.values) {
            for (const dateFacet of response.dateFacetResults[0].values) {
                audienceSum += dateFacet.aggregateFacets.SUM_AUDIENCE_REACH || 0;
                aveSum += dateFacet.aggregateFacets.SUM_AD_VALUE_USD || 0;
            }
        }


        const audienceReachDataPoint = {
            id: ChartMetricsEnum.audienceReach as string,
            label: this.localizationService.transform('Reports.audienceReach'),
            csvLabel: this.localizationService.transform('Reports.audienceReach.csv'),
            jsonLabel: this.localizationService.transform('Reports.audienceReach.json'),
            xValue: audienceSum,
            numberFormat: '1.0',
            useSignificantDigits: 1
        };

        let reportCurrency: UserCurrency;
        reportCurrency = series?.styles?.currency;
        if (!reportCurrency && this.preferences) {
            reportCurrency = this.preferences.currency;
        }

        aveSum = +this.currencyConversionService.convertCurrency(aveSum, false, reportCurrency);


        const impressionsDataPoint = {
            id: ChartMetricsEnum.IMPRESSIONS as string,
            label: this.localizationService.transform('Reports.audienceReach'),
            csvLabel: this.localizationService.transform('Reports.audienceReach.csv'),
            jsonLabel: this.localizationService.transform('Reports.audienceReach.json'),
            xValue: audienceSum,
            numberFormat: '1.0',
            useSignificantDigits: 1
        };

        const aveDataPoint = {
            id: ChartMetricsEnum.ave as string,
            label: this.localizationService.transform('Reports.ave') + `(${reportCurrency})`,
            csvLabel: this.localizationService.transform('Reports.ave.csv') + `(${reportCurrency})`,
            jsonLabel: this.localizationService.transform('Reports.ave.json') + `(${reportCurrency})`,
            xValue: aveSum,
            currency: reportCurrency || this.currencyConversionService.userCurrency,
            useSignificantDigits: 1
        };

        const positiveDataPoint: DataPointModel = {
            id: ChartMetricsEnum.positiveSentimentPercentage,
            label: this.localizationService.transform('Reports.positiveSentimentPercentage'),
            csvLabel: this.localizationService.transform('Reports.positiveSentimentPercentage.csv'),
            jsonLabel: this.localizationService.transform('Reports.positiveSentimentPercentage.json'),
            xValue: 0,
            percent: 0,
        };
        const neutralDataPoint: DataPointModel = {
            id: ChartMetricsEnum.neutralSentimentPercentage,
            label: this.localizationService.transform('Reports.neutralSentimentPercentage'),
            csvLabel: this.localizationService.transform('Reports.neutralSentimentPercentage.csv'),
            jsonLabel: this.localizationService.transform('Reports.neutralSentimentPercentage.json'),
            xValue: 0,
            percent: 0,
        };
        const negativeDataPoint: DataPointModel = {
            id: ChartMetricsEnum.negativeSentimentPercentage,
            label: this.localizationService.transform('Reports.negativeSentimentPercentage'),
            csvLabel: this.localizationService.transform('Reports.negativeSentimentPercentage.csv'),
            jsonLabel: this.localizationService.transform('Reports.negativeSentimentPercentage.json'),
            xValue: 0,
            percent: 0,
        };

        const totalMentions = response.totalResults;
        const sentimentResults = this.getSentimentValuesFromResponse(response.facetResults);

        positiveDataPoint.xValue = parseFloat((safeDivide(sentimentResults.positive, totalMentions) * 100).toFixed(1));
        neutralDataPoint.xValue = parseFloat((safeDivide(sentimentResults.neutral, totalMentions) * 100).toFixed(1));
        negativeDataPoint.xValue = parseFloat((safeDivide(sentimentResults.negative, totalMentions) * 100).toFixed(1));

        const engagementsTotalDataPoint: DataPointModel = {
            id: ChartMetricsEnum.ENGAGEMENTS,
            label: this.localizationService.transform('Reports.engagements'),
            csvLabel: this.localizationService.transform('Reports.engagements.csv'),
            jsonLabel: this.localizationService.transform('Reports.engagements.json'),
            xValue: 0,
            numberFormat: '1.0',
            useSignificantDigits: 1
        };

        const engagementsPerPostDataPoint: DataPointModel = {
            id: ChartMetricsEnum.ENGAGEMENTS_PER_POST,
            label: this.localizationService.transform('Reports.engagementsPerPost'),
            csvLabel: this.localizationService.transform('Reports.engagementsPerPost.csv'),
            jsonLabel: this.localizationService.transform('Reports.engagementsPerPost.json'),
            xValue: 0,
            numberFormat: '1.0',
            useSignificantDigits: 1
        };

        const engagementRateDataPoint: DataPointModel = {
            id: ChartMetricsEnum.ENGAGEMENT_RATE,
            label: this.localizationService.transform('Reports.engagementRate'),
            csvLabel: this.localizationService.transform('Reports.engagementRate.csv'),
            jsonLabel: this.localizationService.transform('Reports.engagementRate.json'),
            xValue: 0,
            percent: 0,
        };

        const engagementsApplauseDataPoint: DataPointModel = {
            id: ChartMetricsEnum.APPLAUSE,
            label: this.localizationService.transform('Reports.engagements.applause'),
            csvLabel: this.localizationService.transform('Reports.engagements.applause.csv'),
            jsonLabel: this.localizationService.transform('Reports.engagements.applause.json'),
            xValue: 0,
            numberFormat: '1.0',
            useSignificantDigits: 1
        };

        const engagementsConversationDataPoint: DataPointModel = {
            id: ChartMetricsEnum.CONVERSATION,
            label: this.localizationService.transform('Reports.engagements.conversation'),
            csvLabel: this.localizationService.transform('Reports.engagements.conversation.csv'),
            jsonLabel: this.localizationService.transform('Reports.engagements.conversation.json'),
            xValue: 0,
            numberFormat: '1.0',
            useSignificantDigits: 1
        };

        const engagementsAmplificationDataPoint: DataPointModel = {
            id: ChartMetricsEnum.AMPLIFICATION,
            label: this.localizationService.transform('Reports.engagements.amplification'),
            csvLabel: this.localizationService.transform('Reports.engagements.amplification.csv'),
            jsonLabel: this.localizationService.transform('Reports.engagements.amplification.json'),
            xValue: 0,
            numberFormat: '1.0',
            useSignificantDigits: 1
        };

        const engagementsViewsDataPoint: DataPointModel = {
            id: ChartMetricsEnum.VIEWS,
            label: this.localizationService.transform('Reports.engagements.views'),
            csvLabel: this.localizationService.transform('Reports.engagements.views.csv'),
            jsonLabel: this.localizationService.transform('Reports.engagements.views.json'),
            xValue: 0,
            numberFormat: '1.0',
            useSignificantDigits: 1
        };

        const engagementsResponse = response.neoFacetResults?.find(neoFacet => neoFacet.key === ReportsNeoFacetKeys.mediaTypeEngagements);

        // Total Engagements
        const totalEngagements = engagementsResponse?.buckets?.reduce(
            (sum, bucket) => sum + ((bucket.buckets[ReportsNeoFacetKeys.engagementTotal] as { count: number }[])?.[0]?.count ?? 0),
            0
        ) ?? 0;
        engagementsTotalDataPoint.xValue = totalEngagements;

        // Applasue: Likes
        const applause = engagementsResponse?.buckets?.reduce(
            (sum, bucket) => sum + ((bucket.buckets[ReportsNeoFacetKeys.engagementTotalLikes] as { count: number }[])?.[0]?.count ?? 0),
            0
        ) ?? 0;
        engagementsApplauseDataPoint.xValue = applause;

        // Conversation: Replies + Quotes
        const conversation = engagementsResponse?.buckets?.reduce(
            (sum, bucket) => sum
                + ((bucket.buckets[ReportsNeoFacetKeys.engagementTotalReply] as { count: number }[])?.[0]?.count ?? 0)
                + ((bucket.buckets[ReportsNeoFacetKeys.engagementTotalQuotes] as { count: number }[])?.[0]?.count ?? 0),
            0
        ) ?? 0;
        engagementsConversationDataPoint.xValue = conversation;

        // Amplification: Shares
        const amplification = engagementsResponse?.buckets?.reduce(
            (sum, bucket) => sum + ((bucket.buckets[ReportsNeoFacetKeys.engagementTotalShare] as { count: number }[])?.[0]?.count ?? 0),
            0
        ) ?? 0;
        engagementsAmplificationDataPoint.xValue = amplification;

        // Views
        const Views = engagementsResponse?.buckets?.reduce(
            (sum, bucket) => sum + ((bucket.buckets[ReportsNeoFacetKeys.engagementTotalView] as { count: number }[])?.[0]?.count ?? 0),
            0
        ) ?? 0;
        engagementsViewsDataPoint.xValue = Views;

        if (totalMentions !== 0) {
            engagementsPerPostDataPoint.xValue = parseFloat((totalEngagements / totalMentions * 100).toFixed(1));
        }

        const audienceReachResponse = response.neoFacetResults?.find(neoFacet => neoFacet.key === ReportsNeoFacetKeys.audienceReach);
        const totalFollowers = audienceReachResponse?.buckets.reduce((sum, bucket) => sum + (bucket.count * Number(bucket.termValue)), 0) ?? 0;

        engagementRateDataPoint.xValue = parseFloat((safeDivide(totalEngagements, totalFollowers) * 100).toFixed(1));
        engagementRateDataPoint.percent = engagementRateDataPoint.xValue;

        data.push(mentionsDataPoint);
        data.push(impressionsDataPoint);
        data.push(audienceReachDataPoint);
        data.push(aveDataPoint);
        data.push(positiveDataPoint);
        data.push(neutralDataPoint);
        data.push(negativeDataPoint);
        data.push(engagementsTotalDataPoint);
        data.push(engagementsPerPostDataPoint);
        data.push(engagementRateDataPoint);
        data.push(engagementsApplauseDataPoint);
        data.push(engagementsConversationDataPoint);
        data.push(engagementsAmplificationDataPoint);
        data.push(engagementsViewsDataPoint);

        // Only include metrics included in series
        return of(metrics.map(metric => data.find(datum => datum.id === metric)));
    }

    /**
     * Prepare data for coverage comparison
     */
    private mapCoverageComparison(
        series: ReportSeriesModel,
        response: MentionsSearchResponseModel
    ): Observable<DataPointModel[]> {
        const neoFacet = response?.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
        const data: DataPointModel[] = [];
        let bucketValue = 0;
        if (neoFacet) {
            neoFacet?.buckets.forEach(bucket => {
                bucketValue += this.getFacetFieldFromNeoFacet(bucket, series.metric);
            });
        }
        if (isNaN(bucketValue)) {
            bucketValue = 0;
        }
        data.push({
            // use a unique time string as Id to deal with sources with indentical labels
            id: series.id ?  (series.id + '') : (new Date()).valueOf() * Math.random() + '',
            label: series.label,
            xValue: bucketValue,
            viewUrl: series.getMentionSeriesUrl(),
            viewPreviousUrl: series.getMentionSeriesUrl(null, true)
        });

        return of(data);
    }

    private mapXValueByFacet(facetValue: FacetValueModel, type: MentionSearchFacetTypesEnum): number {
        let xValue: number;

        switch (type) {
            case MentionSearchFacetTypesEnum.outletByAVE:
            case MentionSearchFacetTypesEnum.outletByAudienceReach:
            case MentionSearchFacetTypesEnum.authorByAVE:
            case MentionSearchFacetTypesEnum.authorByAudienceReach:
                xValue = facetValue.sum;
                break;
            case MentionSearchFacetTypesEnum.outletByDomainAuthority:
                xValue = facetValue.metricValue;
                break;
            default:
                xValue = facetValue.count;
        }

        return xValue;
    }

    private mapSocialCoverageByNetwork(
        series: ReportSeriesModel,
        response: MentionsSearchResponseModel
    ): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];

        const metric = series?.metric;
        const networkSelection = series?.styles?.networkSelection;

        if (networkSelection?.length) {
            if (response && response.neoFacetResults) {
                const neoFacetField = ReportsNeoFacetKeys.socialCoverageByNetwork;
                const neoFacetResults: NeoFacetResponseModel = response.neoFacetResults?.find(neofacetType => {
                    return neofacetType.key === neoFacetField;
                });

                if (!neoFacetResults) {
                    return of(data);
                }

                for (const neoFacetValue of neoFacetResults.buckets) {
                    if (!neoFacetValue.termValue || neoFacetValue.termValue.trim() === '') {
                        // Ignore facets with empty labels
                        continue;
                    }

                    let xValue: number = 0;
                    if (
                        (neoFacetValue.termValue === 'TWEET' && includes(networkSelection, MediaTypeEnum.TWITTER)) ||
                        includes(networkSelection, neoFacetValue.termValue)
                    ) {
                        xValue = this.getMetricValue(neoFacetValue, metric);

                        let mediaType = neoFacetValue.termValue;
                        // map 'TWEET' for backwards compatibility
                        if (neoFacetValue.termValue === 'TWEET') {
                            mediaType = MediaTypeEnum.TWITTER;
                        }
                        const s = series.clone();
                        s.mentions.combinedQuery.includedMediaTypes = [new MediaTypeModel(mediaType as MediaTypeEnum)];

                        const dataPoint: DataPointModel = {
                            id: mediaType,
                            label: this.localizationService.transform(`MediaType.${mediaType}`),
                            xValue: xValue,
                            // for identifying source on chart segment click later
                            viewUrl: s.getMentionSeriesUrl(),
                            viewPreviousUrl: s.getMentionSeriesUrl(null, true)
                        };
                        data.push(dataPoint);
                    }
                }
            }
        }

        return of(data);
    }

    private getMetricValue(neoFacetValue, metric: ChartMetricsEnum): number {
        let value: number = 0;
        switch (metric) {
            case ChartMetricsEnum.volumeOfMentions:
                value = neoFacetValue.count;
                break;
            case ChartMetricsEnum.IMPRESSIONS:
            case ChartMetricsEnum.audienceReach:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions].length
                ) {
                    value = neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions][0].count || 0;
                }
                break;
            case ChartMetricsEnum.ENGAGEMENTS:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeEngagements] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeEngagements].length
                ) {
                    value = neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeEngagements][0].count || 0;
                }
                break;
            case ChartMetricsEnum.ENGAGEMENT_RATE:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeEngagements] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeEngagements].length &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions].length
                ) {
                    if (neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions][0].count > 0) {
                        value = ((neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeEngagements][0].count || 0) /
                            neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions][0].count) * 100;
                    }
                }
                break;
            case ChartMetricsEnum.LIKES:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalLikes] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalLikes].length
                ) {
                    value = neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalLikes][0].count || 0;
                }
                break;
            case ChartMetricsEnum.CONVERSATION:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions].length
                ) {
                    value = neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalReply][0].count || 0;
                    value += neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalQuotes][0].count || 0;
                }
                break;
            case ChartMetricsEnum.SHARES:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalShare] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalShare].length
                ) {
                    value = neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalShare][0].count || 0;
                }
                break;
            case ChartMetricsEnum.VIEWS:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalView] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalView].length
                ) {
                    value = neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalView][0].count || 0;
                }
                break;
            default:
                // do nothing
        }

        return value;
    }

    private isValueSum(type: MentionSearchFacetTypesEnum): boolean {
        const valuesBySum: MentionSearchFacetTypesEnum[] = [
            MentionSearchFacetTypesEnum.outletByAVE,
            MentionSearchFacetTypesEnum.outletByAudienceReach,
            MentionSearchFacetTypesEnum.authorByAVE,
            MentionSearchFacetTypesEnum.authorByAudienceReach
        ];

        return valuesBySum.includes(type);
    }

    private getSentimentValuesFromResponse(facetResults: FacetResponseModel[]): {
        positive: number;
        neutral: number;
        negative: number;
        notAvailable: number;
    } {
        const results = {
            positive: 0,
            neutral: 0,
            negative: 0,
            notAvailable: 0
        };
        for (const facetType of facetResults) {
            if (facetType.field !== MentionSearchFacetTypesEnum.sentiment) {
                continue;
            }

            for (const facetValue of facetType.values) {
                const floatValue = parseFloat(facetValue.value);

                if (isNaN(floatValue)) {
                    results.notAvailable += facetValue.count;
                    continue;
                }

                // Negative -1 to -0.3
                // Positive 0.3 to 1
                // Neutral -0.2 to 0.2
                if (floatValue <= -0.3) {
                    results.negative += facetValue.count;
                } else if (floatValue >= 0.3) {
                    results.positive += facetValue.count;
                } else {
                    results.neutral += facetValue.count;
                }
            }
        }
        return results;
    }

    /**
    * Prepare data for top terms
    */
    private mapTopTerms(
        series: ReportSeriesModel,
        response: MentionsSearchResponseModel
    ): Observable<DataPointModel[]> {
        let values = [];
        const data = [];

        if (series.termType === TermTypesEnum.WORDS && series.metric === ChartMetricsEnum.VOLUME) {
            if (response?.wordCloudResults?.length) {
                values = response.wordCloudResults[0]?.scores?.slice(0, series.numOfItems) ?? [];

                values.forEach((item, index) => {
                    const searchCriteria = new MentionSearchCriteriaModel();
                    searchCriteria.query.dateQuery = series.dateRange;
                    searchCriteria.query.keywordGroups = [new MonitoringKeywordGroupModel()];
                    searchCriteria.query.keywordGroups[0].or.push(item.term);
                    data.push({
                        id: index + '',
                        label: item.term,
                        count: item.score,
                        tag: item.term,
                        viewMentionUrl: series.getMentionSeriesUrl(searchCriteria)
                    });
                });
            }
        } else if ((series.termType === TermTypesEnum.HANDLES || series.termType === TermTypesEnum.HASHTAGS) && series.metric === ChartMetricsEnum.VOLUME) {
            if (response?.neoFacetResults?.length) {
                const buckets = response.neoFacetResults.find(res => res.key === (series.termType + series.metric));
                values = buckets?.buckets?.slice(0, series.numOfItems) ?? [];
                values.forEach((item, index) => {
                    const searchCriteria = new MentionSearchCriteriaModel();
                    searchCriteria.query.dateQuery = series.dateRange;
                    if (series.termType === TermTypesEnum.HASHTAGS) {
                        searchCriteria.query.hashtags.include.push(item.termValue);
                    } else {
                        searchCriteria.query.keywordGroups = [new MonitoringKeywordGroupModel()];
                        searchCriteria.query.keywordGroups[0].or.push(item.termValue);
                    }

                    data.push({
                        id: index + '',
                        label: item.termValue,
                        count: item.count,
                        tag: item.termValue,
                        viewMentionUrl: series.getMentionSeriesUrl(searchCriteria)
                    });
                });
            }
        } else {
            if (response?.neoFacetResults?.length) {
                const buckets = response.neoFacetResults.find(res => res.key === (series.termType + series.metric));
                values = buckets?.buckets?.slice(0, series.numOfItems) ?? [];
                values.forEach((item, index) => {
                    const searchCriteria = new MentionSearchCriteriaModel();
                    searchCriteria.query.dateQuery = series.dateRange;
                    if (series.termType === TermTypesEnum.HASHTAGS) {
                        searchCriteria.query.hashtags.include.push(item.termValue);
                    } else {
                        searchCriteria.query.keywordGroups = [new MonitoringKeywordGroupModel()];
                        searchCriteria.query.keywordGroups[0].or.push(item.termValue);
                    }
                    let count = item.buckets[series.termType + series.metric][0].count;
                    if (series.metric === ChartMetricsEnum.ave){
                        count = +this.currencyConversionService.convertCurrency(count, false, series?.styles?.currency ?? this.preferences.currency);
                    }

                    data.push({
                        id: index + '',
                        label: item.termValue,
                        count: count,
                        tag: item.termValue,
                        viewMentionUrl: series.getMentionSeriesUrl(searchCriteria)
                    });
                });
            }
        }

        return of(data);
    }

    setProminenceStatus(status: ProminenceRecalculationJobStatus): void {
        this.prominenceStatus = status;
    }

    setTimezone(timezone: string): void {
        this.timezone = timezone;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(x => x.unsubscribe());
    }
}

// Order matters, it determines the order of the legend
export const mediaTypeToPieMap = new Map<MediaTypeEnum, PieChartLabelModel>([
    [
        MediaTypeEnum.ONLINE_NEWS,
        {
            'label': `MediaType.${MediaTypeEnum.ONLINE_NEWS}`,
            'color': chartColors.agprOnline,
            'labelColor': chartColors.agprWhite,
            'legendValue': MediaTypeEnum.ONLINE_NEWS
        }
    ],
    [
        MediaTypeEnum.PRINT,
        {
            'label': `MediaType.${MediaTypeEnum.PRINT}`,
            'color': chartColors.agprPrint,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.PRINT
        }
    ],
    [
        MediaTypeEnum.BLOGS,
        {
            'label': `MediaType.${MediaTypeEnum.BLOGS}`,
            'color': chartColors.agprBlog,
            'labelColor': chartColors.agprWhite,
            'legendValue': MediaTypeEnum.BLOGS
        }
    ],
    [
        MediaTypeEnum.PRESS_RELEASE,
        {
            'label': `MediaType.${MediaTypeEnum.PRESS_RELEASE}`,
            'color': chartColors.agprPressRelease,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.PRESS_RELEASE
        }
    ],
    [
        MediaTypeEnum.TV,
        {
            'label': `MediaType.${MediaTypeEnum.TV}`,
            'color': chartColors.agprTelevision,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.TV
        }
    ],
    [
        MediaTypeEnum.RADIO,
        {
            'label': `MediaType.${MediaTypeEnum.RADIO}`,
            'color': chartColors.agprRadio,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.RADIO
        }
    ],
    [
        MediaTypeEnum.FACEBOOK,
        {
            'label': `MediaType.${MediaTypeEnum.FACEBOOK}`,
            'color': chartColors.agprFacebook,
            'labelColor': chartColors.agprWhite,
            'legendValue': MediaTypeEnum.FACEBOOK
        }
    ],
    [
        MediaTypeEnum.INSTAGRAM,
        {
            'label': `MediaType.${MediaTypeEnum.INSTAGRAM}`,
            'color': chartColors.agprInstagram,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.INSTAGRAM
        }
    ],
    [
        MediaTypeEnum.REDDIT,
        {
            'label': `MediaType.${MediaTypeEnum.REDDIT}`,
            'color': chartColors.agprReddit,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.REDDIT
        }
    ],
    [
        MediaTypeEnum.TWITTER,
        {
            'label': `MediaType.${MediaTypeEnum.TWITTER}`,
            'color': chartColors.agprX,
            'labelColor': chartColors.agprWhite,
            'legendValue': MediaTypeEnum.TWITTER
        }
    ],
    [
        MediaTypeEnum.YOUTUBE,
        {
            'label': `MediaType.${MediaTypeEnum.YOUTUBE}`,
            'color': chartColors.agprYoutube,
            'labelColor': chartColors.agprWhite,
            'legendValue': MediaTypeEnum.YOUTUBE
        }
    ],
    [
        MediaTypeEnum.PODCAST,
        {
            'label': `MediaType.${MediaTypeEnum.PODCAST}`,
            'color': chartColors.agprPodcast,
            'labelColor': chartColors.agprWhite,
            'legendValue': MediaTypeEnum.PODCAST
        }
    ],
    [
        MediaTypeEnum.TIKTOK,
        {
            'label': `MediaType.${MediaTypeEnum.TIKTOK}`,
            'color': chartColors.agprTiktok,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.TIKTOK
        }
    ],
    [
        MediaTypeEnum.SNAPCHAT,
        {
            'label': `MediaType.${MediaTypeEnum.SNAPCHAT}`,
            'color': chartColors.agprSnapchat,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.SNAPCHAT
        }
    ],
    [
        MediaTypeEnum.THREADS,
        {
            'label': `MediaType.${MediaTypeEnum.THREADS}`,
            'color': chartColors.agprThreads,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.THREADS
        }
    ],
    [
        MediaTypeEnum.LINKEDIN,
        {
            'label': `MediaType.${MediaTypeEnum.LINKEDIN}`,
            'color': chartColors.agprLinkedIn,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.LINKEDIN
        }
    ],
    [
        MediaTypeEnum.WECHAT,
        {
            'label': `MediaType.${MediaTypeEnum.WECHAT}`,
            'color': chartColors.agprWechat,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.WECHAT
        }
    ],
    [
        MediaTypeEnum.SINA_WEIBO,
        {
            'label': `MediaType.${MediaTypeEnum.SINA_WEIBO}`,
            'color': chartColors.agprSinaWeibo,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.SINA_WEIBO
        }
    ],
    [
        MediaTypeEnum.TWITCH,
        {
            'label': `MediaType.${MediaTypeEnum.TWITCH}`,
            'color': chartColors.agprTwitch,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.TWITCH
        }
    ],
    [
        MediaTypeEnum.PINTEREST,
        {
            'label': `MediaType.${MediaTypeEnum.PINTEREST}`,
            'color': chartColors.agprPinterest,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.PINTEREST
        }
    ],
    [
        MediaTypeEnum.TUMBLR,
        {
            'label': `MediaType.${MediaTypeEnum.TUMBLR}`,
            'color': chartColors.agprTumblr,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.TUMBLR
        }
    ],
    [
        MediaTypeEnum.TELEGRAM,
        {
            'label': `MediaType.${MediaTypeEnum.TELEGRAM}`,
            'color': chartColors.agprTelegram,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.TELEGRAM
        }
    ],
    [
        MediaTypeEnum.WHATSAPP,
        {
            'label': `MediaType.${MediaTypeEnum.WHATSAPP}`,
            'color': chartColors.agprWhatsapp,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.WHATSAPP
        }
    ],
    [
        MediaTypeEnum.VK,
        {
            'label': `MediaType.${MediaTypeEnum.VK}`,
            'color': chartColors.agprVK,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.VK
        }
    ],
    [
        MediaTypeEnum.OTHER,
        {
            'label': `MediaType.${MediaTypeEnum.OTHER}`,
            'color': chartColors.agprOther,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.OTHER
        }
    ],
    [
        MediaTypeEnum.MAGAZINE,
        {
            'label': `MediaType.${MediaTypeEnum.MAGAZINE}`,
            'color': chartColors.agprMagazine,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.MAGAZINE
        }
    ],
    [
        MediaTypeEnum.BLUESKY,
        {
            'label': `MediaType.${MediaTypeEnum.BLUESKY}`,
            'color': chartColors.agprBluesky,
            'labelColor': chartColors.agprBlack,
            'legendValue': MediaTypeEnum.BLUESKY
        }
    ],
]);
